import { ReactElement, useContext, useEffect, useState } from 'react';
import { debounce } from 'lodash';

import {
  getSearchResults,
  ProductState,
  SearchModifier,
  SearchParameters,
} from '@gn-audio/jss-core';

import { getSharedArray, getSharedProperty } from '../../../getters';
import { SearchMenuFields } from '../../../types';
import useFallbackTranslation from '../../../utils/hooks/useFallbackTranslation';
import { DISPLAY_VERSIONS } from '../../MegaMenu';
import { SearchContext } from '../providers';
import { PopularProducts } from './PopularProducts';
import { ProductResults } from './ProductResults';
import { QuickLinksView } from './QuickLinksView';
import { SearchMenuInput } from './SearchMenuInput';

type SearchMenuProps = {
  menuItem: SearchMenuFields;
  isMenuOpened?: boolean;
};

export const productSearchFilter =
  "not (pageUrl eq null and supportPageUrl eq null) and attributes/any(a: a eq 'Product_Portfolio|Jabra') and (attributes/all(a: a ne 'Meta_Refurbished|True') or attributes/any(a: a eq 'Meta_Accessorytype|17'))";

export const SearchMenu = ({ menuItem, isMenuOpened = false }: SearchMenuProps): ReactElement => {
  const searchResultsContext = useContext(SearchContext);
  const { locale: getLocale, t } = useFallbackTranslation();
  const searchResults = searchResultsContext?.searchResults;
  const setSearchResults = searchResultsContext?.setSearchResults;
  const [searchQuery, setSearchQuery] = useState('');

  const [isLoading, setIsLoading] = useState(false);

  const { NumberOfResultsPerPage, NumberOfResultsPerPageShared }: SearchMenuFields = menuItem;

  const data = { fields: menuItem };

  const popularSearches = getSharedArray(data.fields, 'PopularSearches');
  const quickLinks = getSharedArray(data.fields, 'QuickLinks');

  const searchTextPlaceholder = getSharedArray(data.fields, 'SearchTextPlaceholder');
  const inputPlaceholderText = getSharedProperty(searchTextPlaceholder?.fields, 'Phrase');

  const { PopularSearchesContent } = popularSearches.fields;

  const learnMore = getSharedArray(popularSearches?.fields, 'LearnMoreText');
  const learnMoreText = getSharedProperty(learnMore?.fields, 'Phrase');

  const popularSearch = getSharedArray(popularSearches?.fields, 'PopularSearchesText');
  const popularSearchText = getSharedProperty(popularSearch?.fields, 'Phrase');

  const numberOfResultsPerPage = parseInt(
    NumberOfResultsPerPage?.value
      ? (NumberOfResultsPerPage?.value ?? '4')
      : NumberOfResultsPerPageShared?.value || '4'
  );

  useEffect(() => {
    const debouncedHandleSearch = debounce(async () => {
      setIsLoading(true);

      const searchParams: SearchParameters = {
        productSearchFilter: productSearchFilter,
        searchQuery: searchQuery,
        skip: 0,
        searchModifier: SearchModifier.Prioritize,
        include: ProductState.Available,
        numberOfResultsPerPage: numberOfResultsPerPage,
        locale: getLocale(),
      };
      const result = await getSearchResults(searchParams);
      setIsLoading(false);
      if (setSearchResults) {
        setSearchResults(result);
      }
    }, 500);

    if (searchQuery !== '') {
      debouncedHandleSearch();
    } else {
      if (setSearchResults) {
        setSearchResults({ count: 0, items: [] });
        setIsLoading(false);
      }
    }

    return () => {
      debouncedHandleSearch.cancel();
    };
  }, [searchQuery, setSearchResults, numberOfResultsPerPage, getLocale]);

  return (
    <div className={DISPLAY_VERSIONS.DESKTOP}>
      <div
        aria-label={t('topmenu.SearchPage')}
        className={`search-container flex w-full flex-col ${
          isMenuOpened
            ? 'absolute bg-white shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)] lg:block'
            : ' '
        }bg-white px-2`}
      >
        <div className="mb-12 mt-8 flex items-center justify-center">
          <div className="w-96">
            <SearchMenuInput
              busy={isLoading}
              inputPlaceholderText={inputPlaceholderText?.value || ''}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
            />
          </div>
        </div>
        {searchResults && searchResults.count > 0 ? (
          <ProductResults searchQuery={searchQuery} menuItem={menuItem} />
        ) : (
          <>
            <>
              {searchResults && searchResults.items.length === 0 ? (
                <div className="flex w-full items-center justify-center">
                  <span>{searchResults.error ? t('cart.ExceptionDefaultText') : ''}</span>
                </div>
              ) : null}
            </>
            <div className="flex justify-center">
              <div className="flex w-96 flex-col items-center justify-center xs:w-auto">
                <PopularProducts
                  popularSearchesFields={PopularSearchesContent?.fields}
                  learnMoreText={learnMoreText?.value || ''}
                  popularSearchText={popularSearchText?.value || ''}
                />
                <QuickLinksView quickLinks={quickLinks} />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
